import type { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { NoSearchResults } from 'src/components/pages/search/NoSearchResults';
import { CategoryProducts } from 'src/components/shared/category/CategoryProducts';
import { Loading } from 'src/components/shared/elements/loading';
import { Navigator } from 'src/components/shared/elements/navigator';
import { Paginator } from 'src/components/shared/elements/paginator';
import { MainContainer, MainContainerWithSideBar } from 'src/components/shared/layouts/MainContainer';
import { HtmlHead } from 'src/components/shared/meta/HtmlHead';
import { SideMenuBar } from 'src/components/shared/templates/SideMenuBar';
import { KaimonoWebRoutes } from 'src/config/routes';
import { useAttributionTokens } from 'src/hooks/useAttributionToken';
import { TEST_GROUP_PATTERN, useOptimizeNext } from 'src/hooks/useOptimizeNext';
import { usePaginator, usePaginatorForSearch } from 'src/hooks/usePaginator';
import { useSearchProducts } from 'src/hooks/useSearchProducts';
import { I18nContext } from 'src/stores/I18n';
import { initAndPushCloudRetailDataLayer } from 'src/utils/createDataLayerObject';
import { mapProductsForDataLayer } from 'src/utils/productHelper';
import styles from './index.module.scss';

const Search: NextPage = () => {
  const localeResources = useContext(I18nContext);
  const router = useRouter();
  const keyword = useMemo(() => {
    const { i } = router.query;
    const userInput = i as string;
    // セキュリティ観点から改行コードをサニタイズ
    return userInput?.replace(/\n/g, '');
  }, [router]);
  const optimizeNextPattern = useOptimizeNext();
  const vaisrPushedRef = useRef(false);
  const { getTokens, updateSearchToken, clearSuggestToken } = useAttributionTokens();
  const initialPageIndex = parseInt(router.query.page as string, 10) || 1;
  const { pageIndex, setPageIndex, pageIndexClickHandler, routeLoading } = usePaginator();
  const {
    data: searchedProducts,
    isLoading,
    totalPageCount,
    attributionToken: searchToken
  } = useSearchProducts(encodeURIComponent(keyword), optimizeNextPattern, pageIndex);

  const {
    pageIndex: pageIndexForSearch,
    numPages,
    pageIndexClickHandler: pageIndexClickHandlerForSearch
  } = usePaginatorForSearch({
    products: searchedProducts
  });

  useEffect(() => {
    setPageIndex(initialPageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPageIndex]);

  useEffect(() => {
    pageIndexClickHandlerForSearch(1, false);
    if (searchToken !== getTokens()?.search) vaisrPushedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, pageIndex]);

  useEffect(() => {
    if (optimizeNextPattern === TEST_GROUP_PATTERN && !vaisrPushedRef.current && searchToken && searchedProducts) {
      const completionDetail = getTokens()?.suggest;
      initAndPushCloudRetailDataLayer({
        eventType: 'view_search_results',
        searchQuery: keyword,
        attributionToken: searchToken,
        ...(completionDetail && { completionDetail }),
        productDetails: mapProductsForDataLayer(searchedProducts)
      });
      vaisrPushedRef.current = true;
      updateSearchToken(searchToken);
      clearSuggestToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchToken, searchedProducts]);

  if (routeLoading) return <Loading />;

  const renderProductsAndPaginator = () => {
    if (!searchedProducts || searchedProducts.length === 0) {
      return null;
    }

    const products =
      optimizeNextPattern === TEST_GROUP_PATTERN
        ? searchedProducts
        : searchedProducts.slice((pageIndexForSearch - 1) * 20, pageIndexForSearch * 20);
    const totalPages = optimizeNextPattern === TEST_GROUP_PATTERN ? (totalPageCount ?? 0) : numPages;
    const currentPage = optimizeNextPattern === TEST_GROUP_PATTERN ? pageIndex : pageIndexForSearch;
    const clickHandler =
      optimizeNextPattern === TEST_GROUP_PATTERN ? pageIndexClickHandler : pageIndexClickHandlerForSearch;

    return (
      <div id="goodsCard">
        <CategoryProducts products={products} targetAttributionToken={searchToken} />
        <Paginator
          total={totalPages}
          current={currentPage}
          shouldScroll={!!keyword}
          pageIndexClickHandler={clickHandler}
        />
      </div>
    );
  };

  return (
    <>
      <HtmlHead
        title={`${localeResources.search.meta.title} | ${localeResources.commons.wamazingShop}`}
        description={localeResources.search.meta.description}
        path={KaimonoWebRoutes.search(keyword as string)}
        robotsIndex={false}
      />
      <Navigator
        items={[
          {
            unprefixedPath: KaimonoWebRoutes.searchRoot,
            label: localeResources.search.search
          }
        ]}
      />
      <MainContainer isFlex>
        <SideMenuBar />
        <MainContainerWithSideBar>
          <h1 className={styles.searchTitle}>
            {keyword
              ? isLoading
                ? localeResources.search.searchingFor(keyword)
                : localeResources.search.searchResultFor(keyword)
              : null}
          </h1>
          {renderProductsAndPaginator()}
          {(!searchedProducts || searchedProducts.length === 0) && !isLoading && <NoSearchResults keyword={keyword} />}
        </MainContainerWithSideBar>
      </MainContainer>
    </>
  );
};

export default Search;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = context;
  return {
    props: { locale }
  };
};
